import type { AxiosError, AxiosResponse } from "axios";
import qs from "query-string";
import {
  getCookie,
  hasWindow,
  isDebugAPI,
  logErrorToSentry,
} from "@litres/utils";
import type {
  IApiResponse,
  IGlobalApiParams,
  IReduxRequestNode,
  IResponse,
  IResponseErrorDetails,
} from "@litres-libs/types";
import axios from "../../instance/axios";
import type { IAPIConfig } from "../config/api";
import { ApiError, ApiType } from "../../types/api";
import { getAPITimeout } from "../../api-timeout";

export class AdvertisingPromiseProvider<TParams, TPayload> {
  private constructor(config: IAPIConfig, initialSID: string | undefined) {
    this.SID = initialSID || undefined;
    this.baseURL = config.baseURL || "DEFAULT_BASE_URL";
    this.UIlang = "ru";
  }

  private SID: string | undefined;

  private UIlang: string;

  private baseURL: string;

  private static instance: AdvertisingPromiseProvider<any, any>;

  /**
   * Метод, возвращающий единичный экземпляр синглтона
   * @returns Возвращает провайдер каталита для вызова запросов
   */
  public static getInstance<TParams, TPayload>(
    config: IAPIConfig
  ): AdvertisingPromiseProvider<TParams, TPayload> {
    if (!AdvertisingPromiseProvider.instance) {
      // Передачу конфига не делаем, рассчитываем что мы работаем с одним каталитом по одному конфигу
      // По умолчанию берем SID из куки, но в дальнейшем дадим делать запрос от сида в параметрах запроса
      let initialSID = getCookie("SID");

      // TODO: Сделано, чтобы не порушить тех, кому уже установили DEFAULT_SID. Можно будет убрать к июню 2020
      if (initialSID === "DEFAULT_SID") {
        initialSID = undefined;
      }

      AdvertisingPromiseProvider.instance = new AdvertisingPromiseProvider(
        config,
        initialSID
      );
    }

    return AdvertisingPromiseProvider.instance;
  }

  /**
   * Основной метод вызова запроса
   * @param params - стандартные передаваемые параметры для всех запросов в любые наши API
   * @returns {Promise} - Возвращает пропис запроса каталита
   */
  // TODO: 1. Типизировать ошибку
  // TODO: 2. Возвращать Promise.resolve/reject, сейчас error не падает в catch снаружи
  // TODO: 3. Убрать от await везде. На уровень выше уже есть await
  public async makeAxiosRequest(
    params: IGlobalApiParams<TParams>
  ): Promise<IReduxRequestNode<TParams, TPayload>> {
    const createHeaders = () => {
      const headers = {} as {
        "Session-Id": string;
        /** Контракт на хэдер  Ui-Language-Code - https://docs.litres.ru/pages/viewpage.action?pageId=76646002 */
        "Ui-Language-Code": string;
        "app-id": string;
      };

      headers["app-id"] = "115";

      if (this.SID) {
        headers["Session-Id"] = `${this.SID}`;
      }

      if (hasWindow) {
        headers["Ui-Language-Code"] =
          getCookie("Ui-Language-Code") ||
          window.location.hostname.split(".").slice(-1)[0];
      } else {
        // Пишем заголовок для запросов некста
        headers["Ui-Language-Code"] = this.UIlang;
      }

      return headers;
    };

    const url =
      params.url && params.url !== "/"
        ? params.url.indexOf("foundation") >= 0
          ? params.url
          : this.baseURL + params.url
        : this.baseURL;
    const method = params.request ? params.request.method : "POST";
    const data = params.request ? params.request.data : {};
    const _params = params.request ? params.request.params : {};
    const headers = {
      ...createHeaders(),
      ...params.headers,
    };

    // Делаем запрос
    const res = axios({
      url,
      method,
      data,
      params: _params,
      headers,
      paramsSerializer: data => qs.stringify(data, { arrayFormat: "none" }),
      withCredentials: true,
      timeout: getAPITimeout("advertising", hasWindow, url),
    })
      .then((response: AxiosResponse<IApiResponse<TPayload>>) => {
        if (isDebugAPI) {
          console.info(
            `%cRES: ${params.provider.id}`,
            "background: #abff5f; padding: 2px 12px; color: #848484",
            response.data,
            params
          );
        }

        if (params.onSuccess) {
          params.onSuccess({
            request: params,
            response: response.data,
          });
        }

        delete params.onError;
        delete params.onSuccess;

        // В случае успешного ответа вернем ответ в fullfill
        return {
          request: params,
          response: response.data,
        };
      })
      .catch((error: AxiosError<IResponse>) => {
        if (error.response?.status === 500) {
          logErrorToSentry(new ApiError("API Error (500)"), {
            contextProperties: [
              "REQUEST_PARAMS",
              {
                url,
                method,
                params: JSON.stringify(_params),
                headers,
                data: JSON.stringify(data),
              },
            ],
            tags: {
              api_name: ApiType.Advertising,
            },
          });
        }

        if (isDebugAPI) {
          console.error(
            `%cERR: ${params.provider.id}`,
            "background: #f55e5e; padding: 2px 12px; color: #fff",
            error
          );
        }

        if (params.onError) {
          params.onError({
            request: params,
            error: {
              status: error.response?.status,
              type: (error.response?.data.error as IResponseErrorDetails).type,
              title: (error.response?.data.error as IResponseErrorDetails)
                .title,
            },
          });
        }

        delete params.onError;
        delete params.onSuccess;
        return {
          request: params,
          error: {
            status: error.response?.status,
            type: (error.response?.data.error as IResponseErrorDetails).type,
            title: (error.response?.data.error as IResponseErrorDetails).title,
          },
        };
      });

    return res as Promise<IReduxRequestNode<TParams, TPayload>>;
  }
}
